import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ArrowLeft, MapPin, RefreshCw } from 'lucide-react';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { usePwaNav } from '../contexts/PwaNavContext';
import { useGeoLocation } from '../hooks/useGeoLocation';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { SearchBar } from '../components/feed/SearchBar';
import { FeedSkeleton } from '../components/feed/FeedSkeleton';
import { FeedEmpty } from '../components/feed/FeedEmpty';
import { MixFeedRenderer } from '../components/feed/MixFeedRenderer';
import { API_URL } from '../utils/api';
import type { FeedMixResponse, FeedMixItem } from '../types/feedMix';
import './FeedPage.css';

const MAX_QUERY_LEN = 60;

export default function FeedPage() {
  const { t } = useTranslation();
  const { isAuthenticated, token } = useCustomerAuth();
  const { navigate: pwaNavigate } = usePwaNav();

  // Auth-wall — customer login (NOT staff /login which is for admin)
  useEffect(() => {
    if (!isAuthenticated) {
      pwaNavigate('/loginclient');
    }
  }, [isAuthenticated, pwaNavigate]);

  // Geo
  const { status: geoStatus, coords } = useGeoLocation(true);
  const geoReady =
    geoStatus === 'granted' ||
    geoStatus === 'denied' ||
    geoStatus === 'timeout' ||
    geoStatus === 'unavailable';

  // Search — debounced 250ms
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => window.clearTimeout(id);
  }, [q]);

  // Mix data
  const [items, setItems] = useState<FeedMixItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [seed, setSeed] = useState<number>(() => Math.floor(Date.now() / 60000));

  const reqIdRef = useRef(0);

  const fetchMix = useCallback(async () => {
    if (!isAuthenticated || !token || !geoReady) return;
    const myId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (coords?.lat != null) params.set('lat', String(coords.lat));
      if (coords?.lng != null) params.set('lng', String(coords.lng));
      if (debouncedQ.length >= 2) params.set('q', debouncedQ.slice(0, MAX_QUERY_LEN));
      params.set('seed', String(seed));
      const { data } = await axios.get<FeedMixResponse>(
        `${API_URL || ''}/api/feed/mix?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (myId !== reqIdRef.current) return;
      setItems(data.items);
    } catch (err) {
      if (myId !== reqIdRef.current) return;
      setError((err as Error).message || 'fetch failed');
      setItems([]);
    } finally {
      if (myId === reqIdRef.current) setLoading(false);
    }
  }, [isAuthenticated, token, geoReady, coords?.lat, coords?.lng, debouncedQ, seed]);

  useEffect(() => {
    fetchMix();
  }, [fetchMix]);

  const triggerRefresh = useCallback(() => {
    setSeed(Date.now());
  }, []);

  const { ref: pullRef, pullDistance, threshold } = usePullToRefresh<HTMLDivElement>({
    onRefresh: triggerRefresh,
    enabled: !loading,
  });

  if (!isAuthenticated) return null;

  const pullProgress = Math.min(1, pullDistance / threshold);

  return (
    <div className="feed-page" ref={pullRef}>
      <header className="feed-header">
        <button
          type="button"
          className="feed-back-btn"
          onClick={() => pwaNavigate('/')}
          aria-label={t('venueLanding.back', { defaultValue: 'Назад' })}
        >
          <ArrowLeft size={18} strokeWidth={2.2} aria-hidden />
        </button>
        <img src="/logo-olso.png" alt="OLSO" className="feed-logo" />
        <div className="feed-header-meta">
          <MapPin size={13} aria-hidden />
          <span>{t(geoStatus === 'granted' ? 'feed.headerNearby' : 'feed.headerDiscover')}</span>
        </div>
        <button
          type="button"
          className="feed-refresh-btn"
          onClick={triggerRefresh}
          disabled={loading}
          aria-label={t('feed.refresh', { defaultValue: 'Обновить' })}
        >
          <RefreshCw size={16} strokeWidth={2.2} className={loading ? 'is-spinning' : ''} aria-hidden />
        </button>
      </header>

      <SearchBar value={q} onChange={(raw) => setQ(raw.slice(0, MAX_QUERY_LEN))} />

      {pullDistance > 0 && (
        <div className="feed-pull-indicator" style={{ opacity: pullProgress }}>
          <RefreshCw size={18} style={{ transform: `rotate(${pullProgress * 180}deg)` }} aria-hidden />
        </div>
      )}

      <div className="feed-body">
        {loading && items.length === 0 && <FeedSkeleton />}

        {!loading && error && (
          <div className="feed-error" role="alert">
            <div className="feed-error-msg">
              {t('feed.errorTitle', { defaultValue: 'Не удалось загрузить ленту' })}
            </div>
            <button type="button" className="feed-error-btn" onClick={triggerRefresh}>
              {t('feed.retry', { defaultValue: 'Повторить' })}
            </button>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <FeedEmpty
            variant="search"
            query={debouncedQ || undefined}
            onClearFilters={() => setQ('')}
          />
        )}

        {items.length > 0 && <MixFeedRenderer items={items} />}

        {!loading && !error && items.length > 0 && (
          <div className="feed-end">
            {t('feed.endOfMix', { defaultValue: 'Это всё на сегодня — потяни вниз для обновления' })}
          </div>
        )}
      </div>
    </div>
  );
}
